import type { IntlShape } from 'react-intl';

import type {
  AnalyticalVariable,
  ConceptRef,
  DomainFilter,
  FilterGroup,
  NumericRange,
  TemporalGroup,
} from '@verantos-dev/types-cohorts';
import {
  CriteriaSelection,
  NumericOperator,
  OmopTable,
  TemporalOperator,
} from '@verantos-dev/types-cohorts';

import type { ConceptGroupOption } from '../types';

// ===========================================================================
// Category derivation
// ===========================================================================

export type AnalyticalVariableCategory = 'clinical' | 'demographic' | 'observability';

/**
 * Derives the category of an analytical variable based on the OMOP table(s) used in its logic.
 *
 * Returns `null` when the AV has no structured logic tree (SQL-only AVs from
 * the de novo / agent path). Callers should treat null as "category unknown"
 * and skip rendering the category chip rather than substituting a default.
 *
 * Category mapping:
 * - Demographic: person table
 * - Observability: observation_period, payer_plan_period, episode, episode_event
 * - Clinical: all other tables (condition_occurrence, drug_exposure, procedure_occurrence, etc.)
 */
export function deriveCategoryFromAnalyticalVariable(
  analyticalVariable: AnalyticalVariable
): AnalyticalVariableCategory | null {
  if (!analyticalVariable.logic) return null;

  const tables = extractOmopTables(analyticalVariable.logic);

  if (tables.some(isObservabilityTable)) {
    return 'observability';
  }
  if (tables.some(isDemographicTable)) {
    return 'demographic';
  }

  return 'clinical';
}

function extractOmopTables(
  logic: DomainFilter | FilterGroup | TemporalGroup | null | undefined
): OmopTable[] {
  if (!logic) return [];
  if ('table' in logic) {
    return [logic.table];
  }
  if ('filters' in logic) {
    return (logic.filters as Array<DomainFilter | FilterGroup | TemporalGroup>).flatMap(
      extractOmopTables
    );
  }
  return [];
}

function isDemographicTable(table: OmopTable): boolean {
  return table === OmopTable.PERSON;
}

function isObservabilityTable(table: OmopTable): boolean {
  return [
    OmopTable.OBSERVATION_PERIOD,
    OmopTable.PAYER_PLAN_PERIOD,
    OmopTable.EPISODE,
    OmopTable.EPISODE_EVENT,
  ].includes(table);
}

/** Returns the available role options for an AV based on its category and index date status. */
export function getAvailableRoleOptions(
  category: AnalyticalVariableCategory,
  isIndexDate: boolean
): CriteriaSelection[] {
  if (isIndexDate) return [];
  if (category === 'observability')
    return [CriteriaSelection.INCLUSION, CriteriaSelection.NOT_APPLIED];
  return [CriteriaSelection.INCLUSION, CriteriaSelection.EXCLUSION, CriteriaSelection.NOT_APPLIED];
}

// ===========================================================================
// Concept group ref utilities
// ===========================================================================

/** Parse a CONCEPT_GROUP::"<id>" ref string to extract the raw id. */
export const parseConceptGroupRefId = (refId: string): string | undefined => {
  const match = refId.match(/^CONCEPT_GROUP::"(.+)"$/);
  return match?.[1];
};

interface ConceptGroupMatch {
  names: string[];
  ids: string[];
}

/**
 * Extract concept group identifiers from the AV logic.conceptRef field.
 *
 * Supported shapes:
 *  - `concept_group_list` → `names` array (matched by name)
 *  - `concept_group`      → `name` string (matched by name) + `refIds` (matched by id)
 */
export const extractLogicConceptGroupRefs = (
  logic: AnalyticalVariable['logic']
): ConceptGroupMatch => {
  const empty: ConceptGroupMatch = { names: [], ids: [] };
  if (!logic) return empty;

  const conceptRef = (logic as Record<string, unknown>).conceptRef as
    | Record<string, unknown>
    | undefined;
  if (!conceptRef) return empty;

  if (conceptRef.type === 'concept_group_list' && Array.isArray(conceptRef.names)) {
    return { names: conceptRef.names as string[], ids: [] };
  }

  if (conceptRef.type === 'concept_group') {
    const names: string[] = [];
    const ids: string[] = [];

    if (typeof conceptRef.name === 'string' && conceptRef.name) {
      names.push(conceptRef.name);
    }

    if (Array.isArray(conceptRef.refIds)) {
      for (const refId of conceptRef.refIds as string[]) {
        const parsed = parseConceptGroupRefId(refId);
        ids.push(parsed ?? refId);
      }
    }

    return { names, ids };
  }

  return empty;
};

export interface ConceptGroupMaps {
  /** id → name */
  idToNameMap: Map<string, string>;
  /** lowercase name → id */
  nameToIdMap: Map<string, string>;
  /** lowercase name → owner */
  nameToOwnerMap: Map<string, string | undefined>;
}

/** Build all commonly-needed concept group lookup maps in a single pass. */
export const buildConceptGroupMaps = (
  conceptGroups: ConceptGroupOption[] | undefined
): ConceptGroupMaps => {
  const idToNameMap = new Map<string, string>();
  const nameToIdMap = new Map<string, string>();
  const nameToOwnerMap = new Map<string, string | undefined>();

  conceptGroups?.forEach((cg) => {
    idToNameMap.set(cg.conceptGroupId, cg.conceptGroupName);
    const lowerName = cg.conceptGroupName.toLowerCase();
    nameToIdMap.set(lowerName, cg.conceptGroupId);
    nameToOwnerMap.set(lowerName, cg.owner);
  });

  return { idToNameMap, nameToIdMap, nameToOwnerMap };
};

export const resolveInitialConceptGroupIds = (
  av: AnalyticalVariable,
  conceptGroups: ConceptGroupOption[]
): string[] => {
  const refs = extractLogicConceptGroupRefs(av.logic);
  const { nameToIdMap } = buildConceptGroupMaps(conceptGroups);

  // Direct id matches from refIds
  const idSet = new Set(refs.ids);
  const matchedById = conceptGroups
    .filter((cg) => idSet.has(cg.conceptGroupId))
    .map((cg) => cg.conceptGroupId);

  // Name-based matches (case-insensitive) via shared map
  const matchedByName = refs.names
    .map((n) => nameToIdMap.get(n.toLowerCase()))
    .filter((id): id is string => id != null);

  // Deduplicate, preserving order (id matches first)
  return [...new Set([...matchedById, ...matchedByName])];
};

// ===========================================================================
// Shared types
// ===========================================================================

export type LogicNode = DomainFilter | FilterGroup | TemporalGroup;

// ===========================================================================
// Type guards
// ===========================================================================

export function isDomainFilter(node: LogicNode): node is DomainFilter {
  return 'table' in node && !('operator' in node) && !('filters' in node);
}

export function isFilterGroup(node: LogicNode): node is FilterGroup {
  return 'operator' in node && 'filters' in node;
}

export function isTemporalGroup(node: LogicNode): node is TemporalGroup {
  return 'filters' in node && 'relationship' in node && !('operator' in node);
}

/** Returns the description string of a logic node (FilterGroup, TemporalGroup, or DomainFilter). */
export function getNodeDescription(node: LogicNode): string | undefined {
  if (isFilterGroup(node)) return node.description ?? undefined;
  if (isTemporalGroup(node)) return node.description ?? undefined;
  if (isDomainFilter(node)) {
    return ((node as unknown as Record<string, unknown>).description as string | null) ?? undefined;
  }
  return undefined;
}

/**
 * Returns a display label for any LogicNode — used as the collapsible header text.
 * Prefers the explicit description; falls back to a summary derived from the node content.
 */
export function getNodeLabel(
  intl: IntlShape,
  node: LogicNode,
  options?: { ignoreDescription?: boolean }
): string {
  if (!options?.ignoreDescription) {
    const desc = getNodeDescription(node);
    if (desc) return desc[0].toUpperCase() + desc.slice(1);
  }

  if (isDomainFilter(node)) {
    // TODO: Remove cast when DomainFilter type includes conceptRef field
    const record = node as unknown as Record<string, unknown>;
    const conceptRef = record.conceptRef as ConceptRef | undefined;
    const names = extractConceptNames(conceptRef);
    const domain = getDomainLabel(intl, node.table);
    return names.length > 0 ? `${domain} matching ${names.join(', ')}` : domain;
  }

  if (isTemporalGroup(node)) {
    const childLabels = node.filters.map((f) => getNodeLabel(intl, f as LogicNode)).filter(Boolean);
    // Self-join with a single filter: use the child label as-is (the temporal
    // relationship is shown by the AND + connectors rendered between the nodes)
    if (node.selfJoin && childLabels.length === 1) {
      return childLabels[0];
    }
    if (childLabels.length >= 2) {
      const rel = formatRelationship(
        intl,
        node.relationship,
        node.minGap,
        node.maxGap,
        node.timeUnit
      );
      return childLabels.join(` ${rel.toLowerCase()} `);
    }
    return childLabels[0] || formatTableName('filter');
  }

  if (isFilterGroup(node)) {
    const childLabels = node.filters.map((f) => getNodeLabel(intl, f as LogicNode)).filter(Boolean);
    if (childLabels.length > 0 && childLabels.length <= 3) {
      const sep = ` ${node.operator.toUpperCase()} `;
      return childLabels.join(sep);
    }
    return `${node.operator.toUpperCase()} (${node.filters.length} filters)`;
  }

  return formatTableName('filter');
}

/**
 * Builds the chip label for a self-join TemporalGroup from its structural fields.
 * Returns undefined if the group is not a self-join or has no description set.
 */
export function formatSelfJoinChipLabel(group: TemporalGroup): string | undefined {
  if (!group.selfJoin || !group.description) return undefined;

  const concept = group.description[0].toUpperCase() + group.description.slice(1);
  const { minGap, maxGap, relationship } = group;

  let gapStr = '';
  if (minGap != null && maxGap != null) {
    gapStr = `, ${minGap}-${maxGap} days apart`;
  } else if (minGap != null) {
    gapStr = `, >=${minGap} days apart`;
  } else if (maxGap != null) {
    gapStr =
      relationship === TemporalOperator.WITHIN
        ? `, within ${maxGap} days`
        : `, <=${maxGap} days apart`;
  }

  return `>=2 ${concept} encounters${gapStr}`;
}

// ===========================================================================
// Concept name extraction
// ===========================================================================

/** Sentinel value used when a concept group ID cannot be resolved to a name. */
export const UNRESOLVED_CONCEPT_GROUP_PLACEHOLDER = '[Unavailable]';

export function extractConceptNames(
  conceptRef: ConceptRef | undefined | null,
  conceptGroupIdToNameMap?: Map<string, string>
): string[] {
  if (!conceptRef) return [];

  let names: string[];

  switch (conceptRef.type) {
    case 'concept_group_list':
      names = conceptRef.names ?? [];
      break;
    case 'concept_group': {
      const refIds = conceptRef.refIds ?? [];
      if (conceptGroupIdToNameMap && refIds.length > 0) {
        names = refIds.map((refId) => {
          // Try the raw refId first, then parse the CONCEPT_GROUP::"<id>" wrapper
          const rawId = parseConceptGroupRefId(refId) ?? refId;
          return (
            conceptGroupIdToNameMap.get(rawId) ??
            conceptGroupIdToNameMap.get(refId) ??
            conceptRef.name ??
            UNRESOLVED_CONCEPT_GROUP_PLACEHOLDER
          );
        });
      } else {
        names = conceptRef.name ? [conceptRef.name] : [];
      }
      break;
    }
    case 'inline_concept_list':
      names = (conceptRef.concepts ?? [])
        .map((c) => c.conceptName)
        .filter((name): name is string => typeof name === 'string' && name.length > 0);
      break;
    default:
      return [];
  }

  // OR-5861: spread before sorting. `names` may be a direct reference to a
  // frozen array on a ConceptRef pulled from Immer/Redux state; an in-place
  // sort would throw `TypeError: Cannot assign to read only property '0'`
  // and crash the cohort builder into the global error boundary.
  return [...names].sort((a, b) => a.localeCompare(b));
}

/**
 * Resolve concept group names to a ConceptRef object.
 * If all names can be resolved to IDs, produces a `concept_group` ref (resolved).
 * Otherwise falls back to `concept_group_list` (name-only).
 */
export function resolveNamesToConceptRef(
  existing: object,
  names: string[],
  nameToIdMap: Map<string, string>
): object {
  const refIds = names
    .map((n) => nameToIdMap.get(n.toLowerCase()))
    .filter((id): id is string => id != null);

  if (refIds.length === names.length && refIds.length > 0) {
    return { ...existing, type: 'concept_group', refIds, name: names[0] };
  }
  return { ...existing, type: 'concept_group_list', names };
}

/** Returns true if the key is a ConceptRef field (ends with 'ConceptRef' or is exactly 'conceptRef'). */
export function isConceptRefKey(key: string): boolean {
  return key === 'conceptRef' || key.endsWith('ConceptRef');
}

/** Walk a logic tree and return the first node whose `table` matches. */
export function findFilterByTable(
  node: Record<string, unknown>,
  table: string
): Record<string, unknown> | undefined {
  if (node.table === table) return node;
  if (Array.isArray(node.filters)) {
    for (const child of node.filters) {
      const found = findFilterByTable(child as Record<string, unknown>, table);
      if (found) return found;
    }
  }
  return undefined;
}

/** Walk a logic tree and return the node whose `id` matches. */
export function findFilterById(
  node: Record<string, unknown>,
  id: string
): Record<string, unknown> | undefined {
  if (node.id === id) return node;
  if (Array.isArray(node.filters)) {
    for (const child of node.filters) {
      const found = findFilterById(child as Record<string, unknown>, id);
      if (found) return found;
    }
  }
  if (Array.isArray(node.correlatedFilters)) {
    for (const corr of node.correlatedFilters as Array<Record<string, unknown>>) {
      if (corr.filter) {
        const found = findFilterById(corr.filter as Record<string, unknown>, id);
        if (found) return found;
      }
    }
  }
  return undefined;
}

/** Get ordered conceptRef field keys from a filter node (`conceptRef` first, then others). */
export function getConceptRefKeys(node: Record<string, unknown>): string[] {
  const keys: string[] = [];
  if ('conceptRef' in node) keys.push('conceptRef');
  for (const key of Object.keys(node)) {
    if (key !== 'conceptRef' && isConceptRefKey(key) && node[key]) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * Recursively walks a LogicNode tree and collects all concept names from every
 * DomainFilter (including correlated filters). Returns a deduplicated array.
 */
export function collectAllConceptNamesFromLogicTree(
  logic: LogicNode | undefined | null,
  conceptGroupIdToNameMap?: Map<string, string>
): string[] {
  const names: string[] = [];

  function walk(node: LogicNode | undefined | null): void {
    if (!node) return;

    if (isDomainFilter(node)) {
      const record = node as unknown as Record<string, unknown>;
      for (const key of Object.keys(record)) {
        if (isConceptRefKey(key) && record[key] != null) {
          names.push(...extractConceptNames(record[key] as ConceptRef, conceptGroupIdToNameMap));
        }
      }
      // Walk correlated filters
      const rawCorrelated = record.correlatedFilters as Array<{ filter: unknown }> | undefined;
      if (rawCorrelated) {
        for (const corr of rawCorrelated) {
          walk(corr.filter as DomainFilter);
        }
      }
      return;
    }

    if (isFilterGroup(node)) {
      node.filters.forEach(walk);
      return;
    }

    if (isTemporalGroup(node)) {
      node.filters.forEach((f) => walk(f as LogicNode));
    }
  }

  walk(logic);
  return [...new Set(names)];
}

/**
 * Returns concept names that are not present in the owner map (case-insensitive).
 * These represent concept groups referenced in AV logic but not available in the project.
 */
export function getUnresolvedConceptNames(
  allNames: string[],
  conceptGroupNameToOwnerMap: Map<string, string | undefined>
): string[] {
  return allNames.filter((n) => !conceptGroupNameToOwnerMap.has(n.toLowerCase()));
}

/**
 * Recursively checks if logic tree contains any concept groups.
 * Returns true if any DomainFilter in the tree has a concept reference field.
 * Dynamically detects any key ending in 'ConceptRef' so new backend fields are handled automatically.
 */
export function hasConceptGroups(logic: LogicNode | undefined | null): boolean {
  if (!logic) return false;

  if (isDomainFilter(logic)) {
    const record = logic as unknown as Record<string, unknown>;
    return Object.keys(record).some((key) => isConceptRefKey(key) && record[key] != null);
  }

  if (isFilterGroup(logic)) return logic.filters.some(hasConceptGroups);
  if (isTemporalGroup(logic)) return (logic.filters as LogicNode[]).some(hasConceptGroups);

  return false;
}

// ===========================================================================
// Numeric formatting (intl-aware)
// ===========================================================================

const NUMERIC_OPERATOR_SYMBOLS: Record<string, string> = {
  [NumericOperator.EQ]: '=',
  [NumericOperator.NEQ]: '≠',
  [NumericOperator.LT]: '<',
  [NumericOperator.LTE]: '≤',
  [NumericOperator.GT]: '>',
  [NumericOperator.GTE]: '≥',
  at_least: '≥',
  at_most: '≤',
};

function getOperatorSymbol(operator: string): string {
  return NUMERIC_OPERATOR_SYMBOLS[operator] ?? operator;
}

export function formatNumericFilter(filter: NumericRange): string {
  return formatNumericFilterChips(filter).join(', ');
}

export function formatNumericFilterChips(filter: NumericRange): string[] {
  if (filter.operator === NumericOperator.BETWEEN) {
    const lowOp = filter.lowInclusive === false ? '>' : '≥';
    const highOp = filter.highInclusive === false ? '<' : '≤';
    return [`${lowOp} ${filter.low ?? '?'}`, `${highOp} ${filter.high ?? '?'}`];
  }
  if (filter.operator === NumericOperator.NOT_BETWEEN) {
    // NOT_BETWEEN chips show the OUTER conditions (what IS included).
    // NOT (low_op lo AND high_op hi) inverts each side:
    //   default inclusive (None/true) → outer is strict: < lo, > hi
    //   exclusive bound (false) → outer is non-strict: ≤ lo, ≥ hi
    const lowOp = filter.lowInclusive === false ? '≤' : '<';
    const highOp = filter.highInclusive === false ? '≥' : '>';
    return [`${lowOp} ${filter.low ?? '?'}`, `${highOp} ${filter.high ?? '?'}`];
  }

  return [`${getOperatorSymbol(filter.operator)} ${filter.value ?? '?'}`];
}

// ===========================================================================
// Temporal relationship formatting (intl-aware)
// ===========================================================================

export function formatRelationship(
  intl: IntlShape,
  relationship: string | null | undefined,
  minGap?: number | null,
  maxGap?: number | null,
  timeUnit?: string
): string {
  if (!relationship) {
    return intl.formatMessage({ id: 'projects.analyticalVariables.logic.relationship.default' });
  }
  const unit =
    timeUnit ?? intl.formatMessage({ id: 'projects.analyticalVariables.logic.defaultTimeUnit' });
  const rel = relationship.replace(/_/g, ' ').toUpperCase();

  const gapParts: string[] = [];
  if (minGap != null) {
    gapParts.push(
      intl.formatMessage(
        { id: 'projects.analyticalVariables.logic.minGap' },
        { value: minGap, unit }
      )
    );
  }
  if (maxGap != null) {
    gapParts.push(
      intl.formatMessage(
        { id: 'projects.analyticalVariables.logic.maxGap' },
        { value: maxGap, unit }
      )
    );
  }

  if (gapParts.length > 0) {
    return intl.formatMessage(
      { id: 'projects.analyticalVariables.logic.relationshipWithGaps' },
      { relationship: rel, gaps: gapParts.join(', ') }
    );
  }

  return rel;
}

// ===========================================================================
// Temporal connector label — "from X days after occurrence N to Y days …"
// ===========================================================================

export function formatTemporalConnector(
  intl: IntlShape,
  relationship: string | null | undefined,
  minGap: number | null | undefined,
  maxGap: number | null | undefined,
  timeUnit: string | undefined,
  occurrence: number
): string {
  const unit =
    timeUnit ?? intl.formatMessage({ id: 'projects.analyticalVariables.logic.defaultTimeUnit' });

  switch (relationship) {
    case TemporalOperator.BEFORE:
      if (maxGap == null) {
        return intl.formatMessage(
          { id: 'projects.analyticalVariables.logic.temporalAtLeastAfter' },
          { minGap: minGap ?? 0, unit, occurrence }
        );
      }
      return intl.formatMessage(
        { id: 'projects.analyticalVariables.logic.temporalAfterRange' },
        { minGap: minGap ?? 0, maxGap, unit, occurrence }
      );

    case TemporalOperator.ON_OR_BEFORE:
      if (maxGap == null) {
        return intl.formatMessage(
          { id: 'projects.analyticalVariables.logic.temporalOnOrAfter' },
          { occurrence }
        );
      }
      return intl.formatMessage(
        { id: 'projects.analyticalVariables.logic.temporalAfterRange' },
        { minGap: minGap ?? 0, maxGap, unit, occurrence }
      );

    case TemporalOperator.APART:
      return intl.formatMessage(
        { id: 'projects.analyticalVariables.logic.temporalAwayFrom' },
        { minGap: minGap ?? 0, unit, occurrence }
      );

    case TemporalOperator.WITHIN:
      if (minGap != null && maxGap != null) {
        return intl.formatMessage(
          { id: 'projects.analyticalVariables.logic.temporalBetweenOf' },
          { minGap, maxGap, unit, occurrence }
        );
      }
      return intl.formatMessage(
        { id: 'projects.analyticalVariables.logic.temporalWithin' },
        { maxGap: maxGap ?? 0, unit, occurrence }
      );

    case TemporalOperator.BETWEEN:
      return intl.formatMessage(
        { id: 'projects.analyticalVariables.logic.temporalBetweenOf' },
        { minGap: minGap ?? 0, maxGap: maxGap ?? 0, unit, occurrence }
      );

    case TemporalOperator.DURING:
      return intl.formatMessage(
        { id: 'projects.analyticalVariables.logic.temporalDuring' },
        { occurrence }
      );

    case TemporalOperator.OVERLAPS:
      return intl.formatMessage(
        { id: 'projects.analyticalVariables.logic.temporalOverlaps' },
        { occurrence }
      );

    default:
      return formatRelationship(intl, relationship, minGap, maxGap, timeUnit);
  }
}

// ===========================================================================
// Correlated relationship formatting (intl-aware)
// ===========================================================================

const CORRELATED_LABEL_PREFIX = 'projects.analyticalVariables.logic.relationship.';

export function formatCorrelatedRelationship(
  intl: IntlShape,
  relationship: string | null | undefined
): string {
  if (!relationship) {
    return intl.formatMessage({ id: 'projects.analyticalVariables.logic.relationship.default' });
  }
  const id = `${CORRELATED_LABEL_PREFIX}${relationship}`;
  const result = intl.formatMessage({ id, defaultMessage: id });
  return result === id ? relationship.replace(/_/g, ' ').toUpperCase() : result;
}

// ===========================================================================
// Table / domain helpers
// ===========================================================================

export function formatTableName(table: string | null | undefined): string {
  if (!table) return '';
  return table
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const DOMAIN_LABEL_PREFIX = 'projects.analyticalVariables.logic.domain.';
const TABLE_LABEL_PREFIX = 'projects.analyticalVariables.logic.table.';

export function getDomainLabel(intl: IntlShape, table: string | null | undefined): string {
  if (!table) return '';
  const id = `${DOMAIN_LABEL_PREFIX}${table}`;
  const result = intl.formatMessage({ id, defaultMessage: id });
  return result === id ? formatTableName(table) : result;
}

export function getTableLabel(intl: IntlShape, table: string | null | undefined): string {
  if (!table) return '';
  const id = `${TABLE_LABEL_PREFIX}${table}`;
  const result = intl.formatMessage({ id, defaultMessage: id });
  return result === id ? formatTableName(table) : result;
}

// ===========================================================================
// Config-driven DomainFilter field extraction
// ===========================================================================

/** A named concept reference row for v0-style "{domain} matching [chips]" rendering. */
export interface ConceptRefRow {
  label: string;
  conceptRef: ConceptRef | null;
  /** The field key on the DomainFilter (e.g. 'conceptRef', 'visitConceptRef'). */
  fieldKey: string;
  /** OMOP concept column name used to look up domain filtering config (e.g., "condition_type_concept"). */
  conceptAttribute?: string;
}

/**
 * Derives a human-readable label from a ConceptRef field key.
 * Strips the 'ConceptRef' suffix and converts camelCase to title case with spaces.
 * E.g., 'raceConceptRef' → 'Race', 'ethnicityConceptRef' → 'Ethnicity'.
 */
/**
 * Derives the OMOP concept column name from a camelCase field key.
 * E.g., 'conceptRef' on table 'condition_occurrence' → 'condition_concept',
 *        'typeConceptRef' on 'condition_occurrence' → 'condition_type_concept',
 *        'sourceConceptRef' on 'drug_exposure' → 'drug_source_concept'.
 *
 * The prefix is derived from the OMOP table name by taking everything before the
 * first underscore-delimited suffix (_occurrence, _exposure, _era, etc.).
 */

/** Maps OMOP table names to their concept column prefix. */
const OMOP_TABLE_PREFIX: Record<string, string> = {
  condition_occurrence: 'condition',
  condition_era: 'condition',
  drug_exposure: 'drug',
  drug_era: 'drug',
  procedure_occurrence: 'procedure',
  device_exposure: 'device',
  measurement: 'measurement',
  observation: 'observation',
  visit_occurrence: 'visit',
  specimen: 'specimen',
  episode: 'episode',
  person: 'person',
};

/** Maps fieldKey suffixes (camelCase, sans "ConceptRef") to OMOP column infixes. */
const FIELD_KEY_TO_INFIX: Record<string, string> = {
  '': '', // conceptRef → {prefix}_concept
  type: '_type',
  status: '_status',
  source: '_source',
};

/**
 * Special-case overrides for concept attribute names that don't follow the
 * standard {prefix}_{infix}_concept pattern.
 */
/**
 * Special-case overrides for concept attribute names that don't follow the
 * standard {prefix}_{infix}_concept pattern.
 *
 * These fall into two categories:
 * 1. Cross-table refs: visitConceptRef always maps to visit_concept regardless of parent table.
 * 2. Standalone names: route_concept, unit_concept, modifier_concept, qualifier_concept,
 *    operator_concept, value_as_concept — the column name doesn't carry the table prefix.
 */
const CONCEPT_ATTRIBUTE_OVERRIDES: Record<string, Record<string, string>> = {
  condition_occurrence: {
    visitConceptRef: 'visit_concept',
  },
  drug_exposure: {
    routeConceptRef: 'route_concept',
    visitConceptRef: 'visit_concept',
  },
  procedure_occurrence: {
    modifierConceptRef: 'modifier_concept',
    visitConceptRef: 'visit_concept',
  },
  device_exposure: {
    visitConceptRef: 'visit_concept',
  },
  measurement: {
    operatorConceptRef: 'operator_concept',
    unitConceptRef: 'unit_concept',
    unitSourceConceptRef: 'unit_source_concept',
    valueAsConceptRef: 'value_as_concept',
    visitConceptRef: 'visit_concept',
  },
  observation: {
    qualifierConceptRef: 'qualifier_concept',
    unitConceptRef: 'unit_concept',
    valueAsConceptRef: 'value_as_concept',
    visitConceptRef: 'visit_concept',
  },
  provider: {
    specialtyConceptRef: 'specialty_concept',
    specialtySourceConceptRef: 'specialty_source_concept',
  },
  visit_occurrence: {
    admittedFromConceptRef: 'admitted_from_concept',
    dischargedToConceptRef: 'discharged_to_concept',
  },
  specimen: {
    anatomicSiteConceptRef: 'anatomic_site_concept',
  },
  episode: {
    diseaseStatusConceptRef: 'disease_status_concept',
  },
};

export function deriveConceptAttribute(table: string, fieldKey: string): string | undefined {
  // Check for explicit overrides first
  const override = CONCEPT_ATTRIBUTE_OVERRIDES[table]?.[fieldKey];
  if (override) return override;

  const prefix = OMOP_TABLE_PREFIX[table];
  if (!prefix) return undefined;

  let result: string;
  if (fieldKey === 'conceptRef') {
    result = `${prefix}_concept`;
  } else {
    // Strip 'ConceptRef' suffix → e.g., 'typeConceptRef' → 'type'
    const base = fieldKey.replace(/ConceptRef$/, '');
    const infix = FIELD_KEY_TO_INFIX[base];
    if (infix !== undefined) {
      result = `${prefix}${infix}_concept`;
    } else {
      // Fallback: convert camelCase to snake_case and append _concept
      const snake = base.replace(/([A-Z])/g, '_$1').toLowerCase();
      result = `${prefix}_${snake}_concept`;
    }
  }
  return result;
}

function deriveConceptRefLabel(field: string): string {
  const base = field.replace(/ConceptRef$/, '');
  return base
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/** Registry of passthrough NumericRange fields on DomainFilter. */
const NUMERIC_FIELDS: ReadonlyArray<{ field: string; labelId: string }> = [
  { field: 'valueAsNumber', labelId: 'projects.analyticalVariables.logic.fieldValue' },
  { field: 'daysSupply', labelId: 'projects.analyticalVariables.logic.fieldDaysSupply' },
  { field: 'age', labelId: 'projects.analyticalVariables.logic.fieldAge' },
];

/** Map camelCase field name to snake_case for matching additionalFilters field names. */
function camelToSnake(s: string): string {
  return s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

/** Shape of a single correlated filter entry from the IR. */
export interface CorrelatedFilterEntry {
  filter: DomainFilter;
  relationship: string | null;
}

export interface DomainFilterFields {
  conceptRefRows: ConceptRefRow[];
  numericFields: Array<{ field: string; labelId: string; filter: NumericRange }>;
  correlatedFilters: CorrelatedFilterEntry[];
}

/** Extract passthrough fields from a DomainFilter using config-driven field registries. */
export function extractDomainFilterFields(
  intl: IntlShape,
  filter: DomainFilter
): DomainFilterFields {
  // TODO: Remove cast when DomainFilter type includes conceptRef, visitConceptRef, etc.
  const record = filter as unknown as Record<string, unknown>;

  // --- concept ref rows ---
  const conceptRefRows: ConceptRefRow[] = [];

  // Include the conceptRef row when the field exists on the filter,
  // even if null (user removed all concept groups — "+ Add" button stays visible).
  // Filters without a conceptRef field (PersonFilter, DeathFilter, etc.) skip this.
  if ('conceptRef' in record) {
    const mainConceptRef = (record.conceptRef as ConceptRef | null) ?? null;
    conceptRefRows.push({
      label: intl.formatMessage(
        { id: 'projects.analyticalVariables.logic.domainMatching' },
        { domain: getDomainLabel(intl, filter.table) }
      ),
      conceptRef: mainConceptRef,
      fieldKey: 'conceptRef',
      conceptAttribute: deriveConceptAttribute(filter.table, 'conceptRef'),
    });
  }

  // Discover all *ConceptRef fields dynamically (skip 'conceptRef' — handled above).
  // Only include secondary refs that have data — schema-default nulls (sourceConceptRef,
  // typeConceptRef, etc.) are not shown.
  for (const key of Object.keys(record)) {
    if (key === 'conceptRef' || !isConceptRefKey(key)) continue;
    const ref = record[key] as ConceptRef | undefined;
    if (!ref) continue;

    const label = `${deriveConceptRefLabel(key)} matching`;
    conceptRefRows.push({
      label,
      conceptRef: ref,
      fieldKey: key,
      conceptAttribute: deriveConceptAttribute(filter.table, key),
    });
  }

  // --- numeric fields ---
  const numericFields: Array<{ field: string; labelId: string; filter: NumericRange }> = [];

  for (const { field, labelId } of NUMERIC_FIELDS) {
    const value = record[field] as NumericRange | undefined;
    if (value) {
      numericFields.push({ field, labelId, filter: value });
    }
  }

  // --- additional filters (e.g. numeric upper-bound ranges) ---
  const rawAdditional = record.additionalFilters as
    | Array<{ filterType: string; field: string; range?: NumericRange }>
    | undefined;

  if (rawAdditional) {
    for (const af of rawAdditional) {
      if (af.filterType === 'numeric_field' && af.range) {
        const matchingField = NUMERIC_FIELDS.find((nf) => camelToSnake(nf.field) === af.field);
        const labelId = matchingField?.labelId ?? 'projects.analyticalVariables.logic.fieldValue';
        numericFields.push({
          field: `additionalFilters.${af.field}`,
          labelId,
          filter: af.range,
        });
      }
    }
  }

  // --- correlated filters ---
  const correlatedFilters: CorrelatedFilterEntry[] = [];
  const rawCorrelated = record.correlatedFilters as
    | Array<{ filter: unknown; relationship: string }>
    | undefined;
  if (rawCorrelated) {
    for (const corr of rawCorrelated) {
      correlatedFilters.push({
        filter: corr.filter as DomainFilter,
        relationship: corr.relationship,
      });
    }
  }

  return {
    conceptRefRows,
    numericFields,
    correlatedFilters,
  };
}
